<template>
  <div
    v-if="!loading"
    class="ui unstackable centered grid stretched"
    :class="classObject"
  >
    <div v-if="visibleMetrics.length < 1" class="center aligned flex items-center justify-center" style="flex:1">
      <p class="ui small muted text">
        No deliverable stats yet
      </p>
    </div>

    <!-- Each metric has its own platform distribution, including on phones. -->
    <div
      v-else-if="compact && isCompactLayout"
      class="native-compact-stat-row"
    >
      <div
        v-for="type in visibleMetrics"
        :key="`${campaign.id}-native-${type}`"
        class="native-compact-stat"
      >
        <pie-chart
          v-if="chartData[type]"
          height="44px"
          :legend="false"
          :donut="true"
          :colors="chartColors(type)"
          :data="chartData[type]"
          class="native-compact-donut"
        />
        <div class="native-compact-stat-label">
          {{ type | humanize }}
        </div>
        <div class="native-compact-stat-value">
          {{ statsTotals(type) | abbreviateNumber(1000, 1)
          }}{{ type == "engagement_rate" ? "%" : "" }}
        </div>
      </div>
    </div>

    <!-- Native campaign detail (non-compact): numbers lead in a clean 2x2
         grid, with ONE informational donut — the campaign's platform mix,
         drawn from the headline metric only. This is not a shared distribution
         for all metrics. Web keeps the per-metric loop below. -->
    <div v-else-if="isNativeApp" class="native-detail-stats">
      <pie-chart
        v-if="nativeDonutMetric && chartData[nativeDonutMetric]"
        height="48px"
        :legend="false"
        :donut="true"
        :colors="chartColors(nativeDonutMetric)"
        :data="chartData[nativeDonutMetric]"
        class="native-detail-donut"
      />
      <div class="native-detail-grid">
        <div
          v-for="type in visibleMetrics"
          :key="`${campaign.id}-native-detail-${type}`"
          class="native-detail-stat"
        >
          <div class="native-detail-stat-label">
            {{ type | humanize }}
            <span
              v-if="type == 'engagement_rate'"
              data-inverted=""
              data-tooltip="Number of content engagements divided by impressions."
              data-variation="mini"
              class="ui tooltip engagement-rate-tooltip"
              tabindex="0"
            >
              <i class="icon question mark circle grey" />
            </span>
          </div>
          <div class="native-detail-stat-value">
            {{ statsTotals(type) | abbreviateNumber(1000, 1)
            }}{{ type == "engagement_rate" ? "%" : "" }}
          </div>
          <span
            v-if="statsChange(type) > 0"
            class="native-detail-stat-change"
          >
            vs. {{ chartData["prev_month_" + type] | abbreviateNumber(10000, 1) }}
            last month
          </span>
        </div>
      </div>
    </div>

    <div
      v-else
      v-for="type in visibleMetrics"
      :key="`${campaign.id}-${type}`"
      class="column"
    >
      <div
        v-if="chartData[type]"
        :class="{ 'ui flat bordered segment grid': !compact }"
      >
        <div class="four wide desktop column">
          <pie-chart
            height="50px"
            :legend="false"
            :donut="true"
            :colors="chartColors(type)"
            :data="chartData[type]"
          />
        </div>
        <div class="twelve wide desktop column">
          <div class="ui statistic mini">
            <div class="label">
              <span class="ui grey text">
                {{ type | humanize }}
                <span
                  v-if="type == 'engagement_rate'"
                  data-inverted=""
                  data-tooltip="Number of content engagements divided by impressions."
                  data-variation="mini"
                  class="ui tooltip engagement-rate-tooltip"
                  tabindex="0"
                >
                  <i class="icon question mark circle grey" />
                </span>
              </span>
            </div>
            <div class="value">
              {{ statsTotals(type) | abbreviateNumber(1000, 1)
              }}{{ type == "engagement_rate" ? "%" : "" }}
            </div>

            <span
              v-if="statsChange(type) > 0 && !compact"
              class="ui small text"
              :class="{
                green: statsChange(type) > 0.05,
                grey: statsChange(type) < 0.05,
              }"
            >
              vs.
              {{ chartData["prev_month_" + type] | abbreviateNumber(10000, 1) }}
              last month
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Native loading state: a quiet skeleton echoing the resolved layout
       (donut + stat row) instead of a bare "Loading stats ..." sentence —
       craft item 2. Web keeps the text branch below, byte-identical. -->
  <div v-else-if="isNativeApp" class="native-stats-skeleton" aria-hidden="true">
    <span class="native-stats-skeleton-donut" />
    <span v-for="n in 3" :key="`skel-${n}`" class="native-stats-skeleton-stat" />
  </div>
  <div v-else class="ui center aligned meta margin-top-small">
    <p class="ui small muted text">Loading stats ...</p>
  </div>
</template>

<script>
import Vue from "vue/dist/vue.esm";

import Chartkick from "chartkick";
import VueChartkick from "vue-chartkick";
import { sum, mean, round, isEmpty } from "lodash";
import { mapState } from "vuex";
import store from "store";

Vue.use(VueChartkick, { Chartkick });
const $ = window.jQuery;

export default {
  store,
  props: {
    initialChartData: {
      type: Object,
      default() {
        return null;
      },
    },
    campaign: {
      type: Object,
      default() {
        return {};
      },
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const $this = this;
    return {
      chartData: {},
      loading: $this.initialChartData ? false : true,
      // Phone width, tracked reactively. The compact hub card below is chosen
      // by VIEWPORT, not by client -- see isCompactLayout.
      isPhoneWidth:
        typeof window !== "undefined" && window.matchMedia
          ? window.matchMedia("(max-width: 767.98px)").matches
          : false,
    };
  },
  computed: {
    ...mapState("deliverable", {
      colors: "colors",
    }),
    classObject() {
      return {
        compact: this.compact,
        "four column": this.visibleMetrics.length == "4",
        "three column": this.visibleMetrics.length == "3",
        "two column": this.visibleMetrics.length == "2",
        "one column": this.visibleMetrics.length == "1",
      };
    },
    visibleMetrics() {
      let metrics = Object.keys(this.chartData);

      if (this.compact) {
        metrics = metrics.filter((m) => m != "engagement_rate");
      }

      metrics = metrics.filter(
        (m) => !m.includes("prev_month") && !m.includes("total_")
      );

      return metrics;
    },
    isNativeApp() {
      return document.documentElement.hasAttribute("data-hotwire-native");
    },
    // Use the compact per-metric layout for phone web and native cards.
    isCompactLayout() {
      return this.isNativeApp || this.isPhoneWidth;
    },
    // The one donut the native campaign detail draws: the platform split of
    // the headline metric (engagement_rate has no meaningful split ring).
    nativeDonutMetric() {
      return this.visibleMetrics.find((m) => m != "engagement_rate");
    },
  },
  mounted() {
    // Keep isPhoneWidth honest across rotation and desktop resize. Listener is
    // removed in beforeDestroy so a hub page full of cards does not leak one
    // per card.
    if (typeof window !== "undefined" && window.matchMedia) {
      this.phoneQuery = window.matchMedia("(max-width: 767.98px)");
      this.onPhoneQueryChange = (e) => {
        this.isPhoneWidth = e.matches;
      };
      if (this.phoneQuery.addEventListener) {
        this.phoneQuery.addEventListener("change", this.onPhoneQueryChange);
      } else {
        this.phoneQuery.addListener(this.onPhoneQueryChange);
      }
    }

    this.$nextTick(() => {
      this.loadChartData();
    });
  },

  beforeDestroy() {
    if (!this.phoneQuery || !this.onPhoneQueryChange) return;
    if (this.phoneQuery.removeEventListener) {
      this.phoneQuery.removeEventListener("change", this.onPhoneQueryChange);
    } else {
      this.phoneQuery.removeListener(this.onPhoneQueryChange);
    }
  },

  methods: {
    loadChartData() {
      if (this.initialChartData) {
        this.chartData = this.initialChartData;
      } else {
        $.ajax({
          url: `/influencer_hub/${this.campaign.id}/pie_chart_data`,
        }).then(
          (data) => {
            this.chartData = data;
            this.loading = false;
          },
          () => {
            // Never hang on "Loading stats ..." — if the request fails (e.g. a
            // campaign this tenant can't read), clear loading and fall through to
            // the empty state instead of spinning forever.
            this.loading = false;
          }
        );
      }
    },
    statsChange(type) {
      let prev = this.chartData[`prev_month_${type}`];
      let current = this.statsTotals(type);
      return (current - prev) / current;
    },
    statsTotals(t) {
      let total;
      if (t == "engagement_rate") {
        total = this.chartData["total_engagement_rate"];
      } else {
        total = sum(Object.values(this.chartData[t]));
      }

      return total;
    },
    chartColors(type) {
      const r = [];
      Object.keys(this.chartData[type]).forEach((k) => {
        r.push(this.colors[k]);
      });
      return r;
    },
  },
};
</script>

<style lang="scss" scoped>
.ui.statistic.mini {
  max-width: 100%;
  text-overflow: ellipsis;

  .value {
    text-align: left;
    font-weight: 700;
    font-size: 2rem;
  }

  .label {
    text-align: left;
    font-weight: normal;
    font-size: 0.857142857em;
    white-space: nowrap;
    max-width: 100%;
    // overflow: hidden;
    text-overflow: ellipsis;
  }
}

.compact {
  .column {
    text-align: center;
  }

  .ui.statistic.mini {
    text-align: center;
    .label {
      text-align: center;
    }
    .value {
      text-align: center;
      font-weight: 500;
      font-size: initial;
    }
  }
}

// Compact per-metric charts for native and phone campaign cards.
.native-compact-stat-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 0;
}

.native-compact-donut {
  margin: 0 auto 8px;
  width: 44px !important;
  height: 44px !important;
}

.native-compact-stat {
  flex: 1 1 auto;
  min-width: max-content;
  text-align: center;
}

.native-compact-stat-label {
  // 13px is the legibility floor the phone layer holds everything else to
  // (_mobile_layout.scss, docs/mobile-audit/rubric.md section 2). This label
  // names the number under it -- Impressions, Views, Engagements -- so it is
  // the part that has to be readable.
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted, #787a92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.native-compact-stat-value {
  margin-top: 2px;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary, rgba(0, 0, 0, 0.87));
  // One number dialect: the campaign tiles render "176.5K" (Fomantic's
  // .ui.statistic .value uppercases); this hub row said "176.5k". Same
  // abbreviation, same case, everywhere.
  text-transform: uppercase;
}

// Native campaign detail: one 48px donut (platform mix, drawn once) beside
// a numbers-led 2x2 grid — replaces the per-metric donut tiles that repeated
// the same split four times. Markup only renders on native (template
// v-else-if), so no extra scoping is needed to keep web untouched.

// …except the component root, which keeps its .ui.grid classes: Fomantic's
// -1rem grid margins overhang the header card's overflow-x:auto content box
// and clip the donut/labels top-left (the same clipped-donut bug the old
// tile layout hit). Native-only; web keeps Fomantic's margins.
html[data-hotwire-native] .ui.grid.stretched:not(.compact) {
  margin: 0 !important;
}

.native-detail-stats {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 4px 0 6px;
}

.native-detail-donut {
  flex: 0 0 48px;
  width: 48px !important;
  height: 48px !important;
}

.native-detail-grid {
  flex: 1 1 auto;
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 12px;
}

.native-detail-stat {
  min-width: 0;
}

.native-detail-stat-label {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;
  color: var(--text-muted, #787a92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.native-detail-stat-value {
  margin-top: 2px;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary, rgba(0, 0, 0, 0.87));
  white-space: nowrap;
  // One number dialect: "176.5K", never "176.5k" (matches the hub row).
  text-transform: uppercase;
}

.native-detail-stat-change {
  display: block;
  margin-top: 1px;
  font-size: 13px;
  color: var(--text-muted, #787a92);
}

i.circular.pie.chart {
  font-size: 2em;
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.16);
}

// Native loading skeleton (craft item 2): echoes the resolved donut +
// 3-up stat row so the card doesn't reflow when data lands. Opacity-only
// pulse; static under reduced motion (content-shaped, so a still skeleton
// reads fine).
.native-stats-skeleton {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
}

.native-stats-skeleton-donut {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 6px solid var(--border-light, rgba(0, 0, 0, 0.08));
}

.native-stats-skeleton-stat {
  flex: 1 1 0;
  height: 34px;
  border-radius: 6px;
  background: var(--border-light, rgba(0, 0, 0, 0.08));
}

.native-stats-skeleton-donut,
.native-stats-skeleton-stat {
  animation: native-stats-skeleton-pulse 1.6s ease-in-out infinite;
}

@keyframes native-stats-skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .native-stats-skeleton-donut,
  .native-stats-skeleton-stat {
    animation: none;
  }
}

// Unlike the other header stats (whose values speak for themselves), the
// Engagement Rate "?" is the only carrier of its explanation — the native
// tooltip kill (`[data-tooltip]::after { display: none !important }` in
// hotwire_native.scss) leaves it fully inert on touch with no fallback.
// Restore it as a tap target: focusing the icon (tabindex added above)
// reveals the explanation as plain inline text next to the icon, rather
// than reopening Fomantic's absolutely-positioned popup bubble — this
// stat card's own ancestor (`.ui.flat.partner.segment > .content`) is
// deliberately `overflow-x: auto` (Wave B fix for the 564px header
// blowout), which clips a floating popup almost entirely off-screen.
// Web is untouched; hover already reveals the floating bubble there.
html[data-hotwire-native] .engagement-rate-tooltip {
  &:focus,
  &:active {
    outline: none;

    &::before {
      display: none !important;
    }

    &::after {
      display: inline !important;
      position: static !important;
      opacity: 1 !important;
      transform: none !important;
      pointer-events: none;
      margin-left: 6px;
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
      background: none !important;
      font-size: 13px;
      font-weight: normal;
      text-transform: none;
      white-space: normal;
      color: var(--text-muted, #9aa5b1);
    }
  }
}

// Narrow-container layout (phones / native app / narrow mobile web).
// Matches the (max-width: 768px) breakpoint mobile.css.scss is served under.
// The grid is `unstackable`, so the Wave A global stacking rules deliberately
// skip it — stats get their own mobile arrangement here instead.
@media only screen and (max-width: 768px) {
  // Non-compact (campaign detail): the 4-up desktop stat/chart boxes ran off
  // the right edge (docW 564 at 390px) because the outer columns keep their
  // fixed desktop widths and the inner `N wide desktop` columns collapse to
  // Fomantic's 6.25% default below the desktop breakpoint. Stack each metric
  // as a full-width row: donut on the left, label + value on the right.
  .ui.grid.stretched:not(.compact) {
    > .column {
      width: 100% !important;
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
    }

    .ui.segment.grid {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      margin: 0;

      > [class*="four wide desktop"].column {
        flex: 0 0 76px;
        width: 76px !important;
        padding: 0.75rem 0.5rem;
      }

      > [class*="twelve wide desktop"].column {
        flex: 1 1 auto;
        min-width: 0;
        width: auto !important;
        padding: 0.75rem 0.75rem 0.75rem 1rem;
      }
    }
  }

  // Shared guards: values never wrap mid-number, labels degrade to an
  // ellipsis instead of overlapping their neighbors ("IMPRESSMSGAGEMEN").
  .ui.statistic.mini {
    .value {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .label {
      overflow: hidden;
    }
  }

  // (The native campaign-detail 2x2 donut-tile rules that used to live here
  // are gone: native no longer renders the per-metric loop at all — it gets
  // the .native-detail-stats branch above, one donut + a numbers-led grid.)
}

// Very narrow phones (<=360px): shrink type before anything clips.
@media only screen and (max-width: 360px) {
  .compact .ui.statistic.mini {
    .label {
      font-size: 10px;
    }

    .value {
      font-size: 1.2rem !important;
    }
  }
}
</style>
